import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import Button from '@pray/shared/components/ui/Button';
import { ChevronRight } from '@pray/shared/components/ui/Icons/ChevronRight';
import { cn } from '@pray/shared/utils/styles';

import { FeatureTitle } from 'components/pages/public/components/ui/FeatureTitle';
import { CarouselItem, type CarouselApi } from 'components/ui/carousel';
import useAppRoutes from 'hooks/useAppRoutes';

import type { BenefitProps, BenefitsSectionProps } from '../types';
import { CardsCarousel } from './CardsCarousel';

enum Visibility {
  heading = 'heading',
  grid = 'grid',
  carousel = 'carousel',
}

const BenefitCard: React.FC<BenefitProps> = ({ icon, title, description, linkText, className, style }) => {
  const appRoutes = useAppRoutes();
  return (
    <Link
      to={appRoutes.getStarted()}
      data-tapeventname={`Benefits CTA - ${title}`}
      className={cn(
        'group flex min-h-[216px] flex-1 flex-col justify-between gap-6 rounded-xl border border-[#FFFFFF0D] bg-[#171718] p-5 text-pray-secondary ease-in-out duration-300 hover:border-gray-600 hover:bg-gray-700 lg:min-h-[316px] lg:min-w-0 lg:gap-8',
        className
      )}
      style={style}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-6">
          <FeatureTitle
            icon={icon}
            iconClassName="size-12 group-hover:before:ease-in-out group-hover:before:duration-300"
          />
          <div className="flex flex-col gap-4">
            <span className="text-xl font-[650] lg:text-2xl">{title}</span>
            <p className="text-base text-text-secondary">{description}</p>
          </div>
        </div>
      </div>
      {linkText && (
        <Button
          data-tapeventname={`Benefits CTA - ${title}`}
          className="flex items-center py-0 pl-0 font-[650] text-gold-100 group-hover:text-gold-200"
        >
          {linkText}
          <ChevronRight
            width={14}
            height={14}
            className="ml-2 text-gold-100 duration-300 group-hover:translate-x-1 group-hover:text-gold-200"
          />
        </Button>
      )}
    </Link>
  );
};

const BenefitsSection: React.FC<BenefitsSectionProps> = ({ benefitsSectionTitle, benefits }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  // Refs for elements to observe
  const headingRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // State to track visibility of observed elements
  const [visibility, setVisibility] = useState({
    [Visibility.heading]: false,
    [Visibility.grid]: false,
    [Visibility.carousel]: false,
  });

  // Ref to store the observer instance
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const elementsToObserve = [
      { ref: headingRef, id: Visibility.heading },
      { ref: gridRef, id: Visibility.grid },
      { ref: carouselRef, id: Visibility.carousel },
    ];

    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.3, // Trigger when 30% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const targetId = (entry.target as HTMLElement).dataset.observeId;
        if (entry.isIntersecting && targetId) {
          setVisibility((prev) => ({ ...prev, [targetId]: true }));
          // Unobserve after it becomes visible if you want the animation only once
          if (observerRef.current) {
            observerRef.current.unobserve(entry.target);
          }
        }
      });
    };

    // Create the observer instance only once
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(observerCallback, observerOptions);
    }

    const observer = observerRef.current;

    elementsToObserve.forEach(({ ref, id }) => {
      const element = ref.current;
      // Observe only if the element exists and hasn't become visible yet
      if (element && !visibility[id]) {
        element.dataset.observeId = id; // Add data attribute for identification
        observer.observe(element);
      }
    });

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);

    // Clean up listener on component unmount
    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl">
        <h2
          ref={headingRef}
          className={cn(
            'mb-16 text-left opacity-0 text-[1.75rem] font-bold leading-10 text-pray-secondary lg:text-[2.5rem] lg:leading-snug',
            visibility.heading && 'animate-slide-in-left opacity-1'
          )}
        >
          {benefitsSectionTitle}
        </h2>
        <div ref={gridRef} className="hidden lg:grid lg:grid-cols-4 lg:gap-6">
          {benefits.map((benefit) => (
            <BenefitCard
              data-tapeventname={`Benefits CTA - ${benefit.title}`}
              key={benefit.id || `benefit-${benefit.title}`}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
              linkText={benefit.linkText}
              className={cn('opacity-1', visibility.grid && 'animate-slide-in-down opacity-1')}
            />
          ))}
        </div>
        <div ref={carouselRef} className="relative -mx-4 pb-16 md:-mx-10 lg:hidden">
          <CardsCarousel count={count} current={current} setApi={setApi} api={api}>
            {benefits.map((benefit, index) => (
              <CarouselItem
                key={index}
                className={cn(
                  'flex basis-[332px] opacity-0 last:mr-6 md:first:ml-6 md:last:mr-10',
                  visibility.carousel && 'opacity-1 animate-slide-in-right'
                )}
              >
                <BenefitCard
                  data-tapeventname={`Benefits CTA - ${benefit.title}`}
                  key={benefit.id || `benefit-${benefit.title}`}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  linkText={benefit.linkText}
                />
              </CarouselItem>
            ))}
          </CardsCarousel>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
